import type {
  AccountFilter,
  AuditFilter,
  ContactFilter,
  EnvironmentId,
  EventFilter,
  RangeId,
  ThreadFilter,
  WebhookDelivery,
} from "@aux0/mock-data";
import { queryOptions } from "@tanstack/react-query";
import { client, clientForProject } from "./index";
import type { Aux0Client } from "./types";

/**
 * Every read in the app, as a `queryOptions` factory.
 *
 * Route loaders call `ensureQueryData` on these and components call
 * `useSuspenseQuery` on the same object, so a navigation prefetches exactly
 * what the component will read and the component never refetches it. Keeping
 * the keys in one file is what stops two screens inventing two keys for one
 * resource and then failing to invalidate each other.
 *
 * The key is always `["<resource>", ...discriminators]` with the filter object
 * last, so `queryClient.invalidateQueries({ queryKey: ["accounts"] })` reaches
 * every variant of a resource.
 */

export function createQueries(client: Aux0Client, scope = "workspace") {
  const options = <T>(value: {
    queryKey: readonly unknown[];
    queryFn: () => Promise<T>;
  }) =>
    queryOptions({
      ...value,
      staleTime: client.kind === "fixtures" ? Infinity : 30_000,
      retry: false,
      queryKey: [...value.queryKey, { adapter: client.kind, scope }],
    });
  return {
    organization: () =>
      options({
        queryKey: ["workspace", "organization"],
        queryFn: () => client.workspace.organization(),
      }),
    currentUser: () =>
      options({
        queryKey: ["workspace", "current-user"],
        queryFn: () => client.workspace.currentUser(),
      }),
    projects: () =>
      options({
        queryKey: ["workspace", "projects"],
        queryFn: () => client.workspace.projects(),
      }),
    members: () =>
      options({
        queryKey: ["workspace", "members"],
        queryFn: () => client.workspace.members(),
      }),
    invitations: () =>
      options({
        queryKey: ["workspace", "invitations"],
        queryFn: () => client.workspace.invitations(),
      }),

    accounts: (filter: AccountFilter = {}) =>
      options({
        queryKey: ["accounts", "list", filter],
        queryFn: () => client.accounts.list(filter),
      }),
    account: (id: string) =>
      options({
        queryKey: ["accounts", "one", id],
        queryFn: async () => (await client.accounts.get(id)) ?? null,
      }),
    accountsNeedingAttention: () =>
      options({
        queryKey: ["accounts", "attention"],
        queryFn: () => client.accounts.needingAttention(),
      }),
    accountStatusCounts: () =>
      options({
        queryKey: ["accounts", "status-counts"],
        queryFn: () => client.accounts.statusCounts(),
      }),
    accountProviderCounts: () =>
      options({
        queryKey: ["accounts", "provider-counts"],
        queryFn: () => client.accounts.providerCounts(),
      }),

    endUsers: () =>
      options({
        queryKey: ["end-users", "list"],
        queryFn: () => client.endUsers.list({ limit: 200 }),
      }),

    hostedAuthLink: (endUserId: string) =>
      options({
        queryKey: ["hosted-auth", endUserId],
        queryFn: () => client.hostedAuth.create(endUserId),
      }),

    mailThreads: (filter: ThreadFilter = { limit: 200 }) =>
      options({
        queryKey: ["mail", "threads", filter],
        queryFn: () => client.mail.threads(filter),
      }),
    mailThread: (id: string) =>
      options({
        queryKey: ["mail", "thread", id],
        queryFn: async () => (await client.mail.thread(id)) ?? null,
      }),
    mailEmails: (threadId: string) =>
      options({
        queryKey: ["mail", "emails", threadId],
        queryFn: () => client.mail.emails(threadId),
      }),
    mailDrafts: (accountId?: string) =>
      options({
        queryKey: ["mail", "drafts", accountId ?? null],
        queryFn: () => client.mail.drafts(accountId),
      }),
    mailFolders: (accountId: string) =>
      options({
        queryKey: ["mail", "folders", accountId],
        queryFn: () => client.mail.folders(accountId),
      }),

    calendars: (accountId?: string) =>
      options({
        queryKey: ["calendar", "calendars", accountId ?? null],
        queryFn: () => client.calendar.calendars(accountId),
      }),
    calendarWeek: () =>
      options({
        queryKey: ["calendar", "week"],
        queryFn: () => client.calendar.week(),
      }),
    calendarEvents: (filter: EventFilter = {}) =>
      options({
        queryKey: ["calendar", "events", filter],
        queryFn: () => client.calendar.events(filter),
      }),
    calendarEvent: (id: string) =>
      options({
        queryKey: ["calendar", "event", id],
        queryFn: async () => (await client.calendar.event(id)) ?? null,
      }),
    calendarBusy: (calendarIds?: string[]) =>
      options({
        queryKey: ["calendar", "busy", calendarIds ?? null],
        queryFn: () => client.calendar.busy(calendarIds),
      }),

    contacts: (filter: ContactFilter = { limit: 200 }) =>
      options({
        queryKey: ["contacts", "list", filter],
        queryFn: () => client.contacts.list(filter),
      }),
    contact: (id: string) =>
      options({
        queryKey: ["contacts", "one", id],
        queryFn: async () => (await client.contacts.get(id)) ?? null,
      }),
    contactGroups: (accountId?: string) =>
      options({
        queryKey: ["contacts", "groups", accountId ?? null],
        queryFn: () => client.contacts.groups(accountId),
      }),

    metrics: () =>
      options({
        queryKey: ["platform", "metrics"],
        queryFn: () => client.platform.metrics(),
      }),
    usage: (range: RangeId) =>
      options({
        queryKey: ["platform", "usage", range],
        queryFn: () => client.platform.usage(range),
      }),
    apiKeys: (environment?: EnvironmentId) =>
      options({
        queryKey: ["platform", "api-keys", environment ?? null],
        queryFn: () => client.platform.apiKeys(environment),
      }),
    webhooks: (environment?: EnvironmentId) =>
      options({
        queryKey: ["platform", "webhooks", environment ?? null],
        queryFn: () => client.platform.webhooks(environment),
      }),
    webhook: (id: string) =>
      options({
        queryKey: ["platform", "webhook", id],
        queryFn: async () => (await client.platform.webhook(id)) ?? null,
      }),
    deliveries: (
      filter: {
        webhookId?: string;
        status?: WebhookDelivery["status"];
        limit?: number;
      } = {},
    ) =>
      options({
        queryKey: ["platform", "deliveries", filter],
        queryFn: () => client.platform.deliveries(filter),
      }),
    auditEvents: (filter: AuditFilter = { limit: 200 }) =>
      options({
        queryKey: ["platform", "audit", filter],
        queryFn: () => client.platform.auditEvents(filter),
      }),

    billingPeriod: () =>
      options({
        queryKey: ["billing", "period"],
        queryFn: () => client.billing.period(),
      }),
    invoices: () =>
      options({
        queryKey: ["billing", "invoices"],
        queryFn: () => client.billing.invoices(),
      }),

    chats: (filter: { accountId?: string; limit?: number } = { limit: 200 }) =>
      options({
        queryKey: ["messaging", "chats", filter],
        queryFn: () => client.messaging.chats(filter),
      }),
    chat: (id: string) =>
      options({
        queryKey: ["messaging", "chat", id],
        queryFn: async () => (await client.messaging.chat(id)) ?? null,
      }),
    chatMessages: (chatId: string) =>
      options({
        queryKey: ["messaging", "messages", chatId],
        queryFn: () => client.messaging.messages(chatId),
      }),
    chatsWithMessages: () =>
      options({
        queryKey: ["messaging", "chats-with-messages"],
        queryFn: () => client.messaging.chatsWithMessages(),
      }),
    profiles: (search?: string) =>
      options({
        queryKey: ["messaging", "profiles", search ?? null],
        queryFn: () => client.messaging.profiles(search),
      }),
    socialInvites: (accountId?: string) =>
      options({
        queryKey: ["messaging", "invites", accountId ?? null],
        queryFn: () => client.messaging.invites(accountId),
      }),
    posts: (accountId?: string) =>
      options({
        queryKey: ["messaging", "posts", accountId ?? null],
        queryFn: () => client.messaging.posts(accountId),
      }),
    postComments: (postId: string) =>
      options({
        queryKey: ["messaging", "comments", postId],
        queryFn: () => client.messaging.comments(postId),
      }),
  };
}

export const q = createQueries(client);
export const qForProject = (project: string) =>
  createQueries(clientForProject(project), project);
